import { useState } from 'react';
import { PLANETS } from '../store/planetsStore';
import { type ScaleMode, useScale } from '../store/scaleStore';
import { type LayerId, useLayers } from '../store/layersStore';

interface LayerSpec {
  id: LayerId;
  label: string;
  status: string;
  help: string;
  disabled?: boolean;
  group: 'confirmed' | 'beyond' | 'hypothesis' | 'future';
}

const LAYERS: LayerSpec[] = [
  { id: 'planets', label: 'Planètes',          status: 'confirmed',
    help: 'Les 8 planètes principales (Mercure → Neptune), positions JPL Horizons.',
    group: 'confirmed' },
  { id: 'dwarfs',  label: 'Planètes naines',   status: 'confirmed',
    help: 'Cérès, Pluton, Haumea, Makemake, Éris (5 corps reconnus par l\'IAU).',
    group: 'beyond' },
  { id: 'etnos',   label: 'ETNO & sednoids',   status: 'confirmed',
    help: 'Objets transneptuniens extrêmes : Sedna, 2017 OF201, 2023 KQ14, etc. Loin (50–1500 UA).',
    group: 'beyond' },
  { id: 'tnos',    label: 'Tous TNO',          status: 'confirmed',
    help: 'Réunit dwarfs + ETNOs (tout corps au-delà de Neptune).',
    group: 'beyond' },
  { id: 'planet9', label: 'Planet 9',          status: 'hypothesis',
    help: '200 orbites candidates générées depuis les priors publiés. NON DÉTECTÉE.',
    group: 'hypothesis' },
  { id: 'alerts',  label: 'Alertes télescopes', status: 'candidate',
    help: 'À venir (v2) : ingestion temps réel des alertes Rubin / SNAPS / IRSA pour les objets mobiles. Non implémenté actuellement.',
    disabled: true,
    group: 'future' },
];

const GROUP_TITLES: Record<LayerSpec['group'], string> = {
  confirmed: 'Confirmé',
  beyond: 'Au-delà de Neptune',
  hypothesis: 'Hypothétique',
  future: 'Bientôt',
};

const SCALE_OPTIONS: { id: ScaleMode; label: string; help: string }[] = [
  { id: 'real',   label: 'Réelle', help: '1 UA = 1 unité (Mercure microscopique)' },
  { id: 'log',    label: 'Log',    help: 'log10(1+r) — comprime tout' },
  { id: 'nested', label: 'Nested', help: 'Paliers inner/outer (lisible)' },
];

export default function LayerPanel() {
  const [search, setSearch] = useState('');
  const scaleMode = useScale((s) => s.mode);
  const setScale = useScale((s) => s.setMode);
  const active = useLayers((s) => s.active);
  const setLayer = useLayers((s) => s.set);

  const matches = search
    ? PLANETS.filter(
        (p) =>
          p.name.toLowerCase().includes(search.toLowerCase()) ||
          p.id.includes(search),
      )
    : [];

  return (
    <div className="layer-panel">
      <h3>Recherche</h3>
      <input
        type="search"
        placeholder="Mercure, 599, Sedna…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{
          width: '100%',
          background: 'var(--bg-2)',
          border: '1px solid var(--line)',
          borderRadius: 8,
          padding: '6px 10px',
          color: 'var(--text-0)',
          fontSize: 12,
        }}
      />
      {matches.length > 0 && (
        <ul style={{ listStyle: 'none', margin: '6px 0 0', padding: 0, fontSize: 12 }}>
          {matches.map((p) => (
            <li key={p.id} style={{ padding: '3px 0', color: 'var(--text-1)' }}>
              <span
                style={{
                  background: p.color,
                  display: 'inline-block',
                  width: 8,
                  height: 8,
                  borderRadius: 4,
                  marginRight: 6,
                }}
              />
              {p.name} <span style={{ color: 'var(--text-dim)' }}>({p.id})</span>
            </li>
          ))}
        </ul>
      )}

      <h3 style={{ marginTop: 14 }}>Échelle</h3>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {SCALE_OPTIONS.map((s) => (
          <button
            key={s.id}
            onClick={() => setScale(s.id)}
            title={s.help}
            style={{
              flex: 1,
              minWidth: 50,
              padding: '5px 8px',
              fontSize: 11,
              borderRadius: 6,
              border: '1px solid var(--line)',
              background: scaleMode === s.id ? 'var(--accent)' : 'var(--bg-2)',
              color: scaleMode === s.id ? '#06080d' : 'var(--text-1)',
              cursor: 'pointer',
              fontWeight: scaleMode === s.id ? 700 : 500,
            }}
          >
            {s.label}
          </button>
        ))}
      </div>

      <h3 style={{ marginTop: 14 }}>Couches</h3>
      {(['confirmed', 'beyond', 'hypothesis', 'future'] as const).map((group) => {
        const groupLayers = LAYERS.filter((l) => l.group === group);
        if (groupLayers.length === 0) return null;
        return (
          <div key={group} style={{ marginBottom: 6 }}>
            <div className="layer-group-title">{GROUP_TITLES[group]}</div>
            {groupLayers.map((l) => (
              <label
                key={l.id}
                className={`layer-row ${l.disabled ? 'disabled' : ''}`}
                title={l.help}
              >
                <span>
                  {l.label}
                  {l.disabled && <span className="layer-soon">à venir</span>}
                </span>
                <input
                  type="checkbox"
                  checked={active[l.id] && !l.disabled}
                  disabled={l.disabled}
                  onChange={(e) => !l.disabled && setLayer(l.id, e.target.checked)}
                />
              </label>
            ))}
          </div>
        );
      })}

      <div className="legend">
        <span><i className="dot confirmed" /> Confirmé</span>
        <span><i className="dot hypothesis" /> Hypothèse</span>
        <span><i className="dot excluded" /> Bientôt</span>
      </div>
    </div>
  );
}
